import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Button } from '../ui/button';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { gameDepartmentAPI } from '../../services/api';
import { SortableHead, TablePagination } from './TableControls';
import useBrandChange from './useBrandChange';
import GameStickinessCurve from './GameStickinessCurve';
import { MAX_COMPARE_GAMES } from './performanceData';

// Positive/CTA brand green — higher return % is the desired outcome, so a single
// sequential hue reads naturally as "more of a good thing" (see bitstarz-ui palette).
const STICKY_COLOR = '#00BD71';
const AXIS_COLOR = '#7a8499';
// Fixed-order categorical palette for the retention-curve lines (identity, not
// magnitude) — brand tokens minus brand-purple, which sits too close to
// brand-blue for colorblind readers to tell apart as adjacent lines.
const CURVE_COLORS = ['#00BD71', '#E21351', '#3861FB', '#FFD22F', '#d4a843'];
const CURVE_TOP_N = CURVE_COLORS.length;

const WINDOW_METRICS = [
  { key: 'pct_return_d1_7',   label: 'Return D1–7' },
  { key: 'pct_return_d8_14',  label: 'Return D8–14' },
  { key: 'pct_return_d15_30', label: 'Return D15–30' },
  { key: 'pct_return_d31_60', label: 'Return D31–60' },
];
// Spend stickiness: how evenly players' turnover/bets are spread over their
// first 60 days (see domain.SpendStickiness). curveMetric is what the cumulative curve plots.
const SPEND_METRICS = [
  { key: 'turnover_stickiness',  label: 'Turnover stickiness', curveMetric: 'turnover' },
  { key: 'bets_stickiness',      label: 'Bets stickiness',     curveMetric: 'n_bets' },
];
const SPEND_KEYS = SPEND_METRICS.map(m => m.key);
const METRICS = [
  ...WINDOW_METRICS,
  { key: 'retention_auc', label: 'Overall (AUC)' },
  ...SPEND_METRICS,
];
const STRING_COLS = new Set(['game_title', 'provider']);
const TOP_N = 12;
const DEFAULT_MIN_PLAYERS = 100;
const TABLE_PAGE_SIZE = 10;

function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(1)}%`; }
function fmtScore(v) { return v == null ? '—' : Number(v).toFixed(1); }
function fmtMetric(key, v) { return key.endsWith('_stickiness') ? fmtScore(v) : fmtPct(v); }
function fmtInt(v) { return Number(v).toLocaleString('en-GB'); }
function fmtAvg(v) { return Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 }); }
function fmtEur(v) { return `€${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`; }
function truncate(s, n) { return s && s.length > n ? `${s.slice(0, n - 1)}…` : s; }

// Area under a game's retention curve — % on the y-axis, the four cohort windows
// as evenly-spaced points on the x-axis — via the trapezoid rule, then divided by
// the curve's total width (3 unit segments) so it lands back on a 0-100 scale and
// reads as one blended "how sticky overall" score instead of a raw area number.
function computeRetentionAuc(g) {
  const y1 = g.pct_return_d1_7;
  const y2 = g.pct_return_d8_14;
  const y3 = g.pct_return_d15_30;
  const y4 = g.pct_return_d31_60;
  const area = (y1 + y2) / 2 + (y2 + y3) / 2 + (y3 + y4) / 2;
  return area / 3;
}

// A game with no returning spend has null spend scores; those sort last in
// either direction rather than being treated as 0.
function compareNullable(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'asc' ? a - b : b - a;
}

function withSpendScores(games, spendGames) {
  const byId = new Map(spendGames.map(g => [g.unique_game_id, g]));
  return games.map(g => {
    const spend = byId.get(g.unique_game_id) || {};
    return {
      ...g,
      retention_auc: computeRetentionAuc(g),
      ...Object.fromEntries(SPEND_KEYS.map(k => [k, spend[k] ?? null])),
    };
  });
}

function StickinessTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] max-w-[220px]">
      <p className="font-bold text-ink mb-1">{g.game_title}</p>
      <p className="text-ink-dim mb-1.5">{g.provider} · {fmtInt(g.num_players)} players</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {METRICS.map(m => (
          <React.Fragment key={m.key}>
            <span className="text-ink-dim">{m.label.replace('Return ', '')}</span>
            <span className="text-ink text-right">{fmtMetric(m.key, g[m.key])}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function CurveTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] min-w-[160px]">
      <p className="font-bold text-ink mb-1.5">{label}</p>
      <div className="flex flex-col gap-1">
        {payload.map(p => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <span className="text-ink-dim truncate flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: p.stroke }} />
              {p.name}
            </span>
            <span className="text-ink shrink-0">{fmtPct(p.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GameStickinessReport() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spendError, setSpendError] = useState(false);

  const [provider, setProvider] = useState('all');
  const [minPlayers, setMinPlayers] = useState(DEFAULT_MIN_PLAYERS);
  const [metric, setMetric] = useState(METRICS[0].key);
  const [bestPerProvider, setBestPerProvider] = useState(false);
  const [sortCol, setSortCol] = useState(METRICS[0].key);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  // Ticked games to open together in Game Performance. Kept across filter,
  // sort and page changes so a comparison can be built up from anywhere in
  // the table.
  const [compareSelection, setCompareSelection] = useState([]);
  const activeBrand = useBrandChange(() => {
    setProvider('all');
    setGames([]);
    setCompareSelection([]);
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    // Spend scores are an add-on: if that query fails the return-rate report
    // still renders, with the spend columns empty and a warning.
    const spendReq = gameDepartmentAPI.getSpendStickiness()
      .then(res => res.data?.games || [])
      .catch(() => { if (!cancelled) setSpendError(true); return []; });
    setSpendError(false);
    Promise.all([gameDepartmentAPI.getGameStickiness(), spendReq])
      .then(([res, spendGames]) => {
        if (cancelled) return;
        setGames(withSpendScores(res.data?.games || [], spendGames));
      })
      .catch(() => { if (!cancelled) setError('Failed to load game stickiness data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeBrand]);

  const providers = useMemo(() => {
    return Array.from(new Set(games.map(g => g.provider).filter(Boolean))).sort();
  }, [games]);

  const filtered = useMemo(() => (
    games.filter(g => g.num_players >= minPlayers && (provider === 'all' || g.provider === provider))
  ), [games, minPlayers, provider]);

  // "Best per provider" swaps the flat leaderboard for one row per provider — its own
  // top game by the selected metric — so a provider with a small catalog isn't simply
  // outnumbered by one with hundreds of titles.
  const ranked = useMemo(() => {
    if (!bestPerProvider) return filtered;
    const byProvider = new Map();
    filtered.forEach(g => {
      const cur = byProvider.get(g.provider);
      if (g[metric] == null) return;
      if (!cur || g[metric] > cur[metric]) byProvider.set(g.provider, g);
    });
    return Array.from(byProvider.values());
  }, [filtered, bestPerProvider, metric]);

  const chartData = useMemo(() => (
    ranked
      .filter(g => g[metric] != null)
      .sort((a, b) => compareNullable(a[metric], b[metric], 'desc'))
      .slice(0, TOP_N)
      .map(g => ({ ...g, label: truncate(g.game_title, 24) }))
  ), [ranked, metric]);

  // The curve reshapes the same top games into one point per cohort window (D1-7 →
  // D31-60), so it plots exactly what the AUC score summarizes — % on the y-axis,
  // cohort window on the x-axis — for the handful of games currently on top.
  const curveGames = useMemo(() => chartData.slice(0, CURVE_TOP_N), [chartData]);

  const curveData = useMemo(() => (
    WINDOW_METRICS.map(w => {
      const point = { window: w.label.replace('Return ', '') };
      curveGames.forEach(g => { point[g.unique_game_id] = g[w.key]; });
      return point;
    })
  ), [curveGames]);

  const tableRows = useMemo(() => {
    const rows = [...ranked];
    rows.sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (!STRING_COLS.has(sortCol)) return compareNullable(av, bv, sortDir);
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [ranked, sortCol, sortDir]);

  // Filtering, ranking or re-sorting reshuffles what "page 1" means, so any of
  // those jumping the user back to a stale page (e.g. now-empty) would be worse
  // than just resetting to the top.
  useEffect(() => { setPage(1); }, [ranked, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(tableRows.length / TABLE_PAGE_SIZE));
  const pagedRows = useMemo(() => (
    tableRows.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE)
  ), [tableRows, page]);

  const activeMetric = METRICS.find(m => m.key === metric);
  const isSpendMetric = Boolean(activeMetric?.curveMetric);

  const spendCurveGames = useMemo(() => curveGames.map((g, i) => ({
    key: `g${g.unique_game_id}`,
    uniqueGameId: g.unique_game_id,
    title: truncate(g.game_title, 22),
    color: CURVE_COLORS[i % CURVE_COLORS.length],
  })), [curveGames]);

  function handleMetricChange(next) {
    setMetric(next);
    setSortCol(next);
    setSortDir('desc');
  }

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  function toPerformanceGame(g) {
    return { unique_game_id: g.unique_game_id, game_title: g.game_title, provider: g.provider };
  }

  function openPerformance(g) {
    navigate('/game-department/performance', { state: { game: toPerformanceGame(g) } });
  }

  const selectedIds = useMemo(() => new Set(compareSelection.map(g => g.unique_game_id)), [compareSelection]);
  const selectionFull = compareSelection.length >= MAX_COMPARE_GAMES;

  function toggleCompare(g) {
    setCompareSelection(sel => {
      if (sel.some(s => s.unique_game_id === g.unique_game_id)) return sel.filter(s => s.unique_game_id !== g.unique_game_id);
      if (sel.length >= MAX_COMPARE_GAMES) return sel;
      return [...sel, toPerformanceGame(g)];
    });
  }

  // Opens straight into the stickiness curve view, since that's what this
  // report's comparison is about.
  function openComparison() {
    navigate('/game-department/performance', { state: { games: compareSelection, view: 'stickiness' } });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Game Stickiness</CardTitle>
        <CardDescription>
          Day-0 cohorts per game: each player&apos;s first play on a game is Day 0, and the return
          rate is the share of that cohort that came back to the same game within the window.
          &quot;Overall (AUC)&quot; blends all four windows into one score — the area under the
          game&apos;s return-rate curve. The spend stickiness metrics score how evenly players&apos;
          turnover/bets are spread over their first 60 days on the game (day 0 excluded): 100 when the
          cumulative curve follows the diagonal, near 0 when it all lands on a single day. They only count
          players whose first bet was more than 60 days before yesterday, so everyone has a full window.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
        {spendError && !error && (
          <Alert variant="error" className="mb-4"><AlertDescription>Failed to load spend stickiness scores.</AlertDescription></Alert>
        )}
        {loading ? (
          <div className="flex justify-center py-10"><Spinner size="sm" /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All providers</SelectItem>
                    {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-ink-dim mb-1 block">Metric</label>
                <Select value={metric} onValueChange={handleMetricChange}>
                  <SelectTrigger className="w-[210px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-ink-dim mb-1 block">Min. players</label>
                <Input
                  type="number"
                  min={0}
                  value={minPlayers}
                  onChange={e => setMinPlayers(Math.max(0, Number(e.target.value) || 0))}
                  className="w-[110px]"
                />
              </div>
              <button
                type="button"
                onClick={() => setBestPerProvider(v => !v)}
                className={`px-3 py-1.5 text-xs rounded-md border cursor-pointer transition-colors ${
                  bestPerProvider ? 'border-brand-yellow text-brand-yellow bg-brand-yellow/10' : 'border-white/10 text-ink-dim'
                }`}
              >
                Best game per provider
              </button>
              <span className="text-[11px] text-ink-dim ml-auto">
                {ranked.length} game{ranked.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="h-[360px] mb-6">
              {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-xs text-ink-dim">
                  No games match these filters
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" tickFormatter={v => fmtMetric(metric, v)} stroke={AXIS_COLOR} fontSize={11} />
                    <YAxis type="category" dataKey="label" width={160} stroke={AXIS_COLOR} fontSize={11} />
                    <Tooltip content={<StickinessTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey={metric} fill={STICKY_COLOR} radius={[0, 4, 4, 0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <p className="text-[11px] font-bold uppercase tracking-wide text-ink-dim mb-2">
              {isSpendMetric ? 'Cumulative spend curve' : 'Retention curve'} — top {curveGames.length} by {activeMetric?.label.toLowerCase()}
            </p>
            {isSpendMetric ? (
              <div className="mb-6">
                {spendCurveGames.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-xs text-ink-dim">
                    No games match these filters
                  </div>
                ) : (
                  <GameStickinessCurve games={spendCurveGames} metric={activeMetric.curveMetric} />
                )}
              </div>
            ) : (
            <div className="h-[300px] mb-6">
              {curveGames.length === 0 ? (
                <div className="flex items-center justify-center h-full text-xs text-ink-dim">
                  No games match these filters
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={curveData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="window" stroke={AXIS_COLOR} fontSize={11} />
                    <YAxis tickFormatter={fmtPct} stroke={AXIS_COLOR} fontSize={11} width={48} />
                    <Tooltip content={<CurveTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={v => <span className="text-ink-dim">{v}</span>} />
                    {curveGames.map((g, i) => (
                      <Line
                        key={g.unique_game_id}
                        type="monotone"
                        dataKey={g.unique_game_id}
                        name={truncate(g.game_title, 22)}
                        stroke={CURVE_COLORS[i % CURVE_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-2 min-h-[32px]">
              <span className="text-[11px] text-ink-dim">
                {compareSelection.length === 0
                  ? `Tick up to ${MAX_COMPARE_GAMES} games to compare them in Game Performance.`
                  : `${compareSelection.length}/${MAX_COMPARE_GAMES} selected: ${compareSelection.map(g => truncate(g.game_title, 22)).join(', ')}`}
              </span>
              {compareSelection.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="ghost" size="sm" onClick={() => setCompareSelection([])}>Clear</Button>
                  <Button size="sm" onClick={openComparison}>
                    Compare {compareSelection.length} in Game Performance
                  </Button>
                </div>
              )}
            </div>

            <div className="border border-white/10 rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"><span className="sr-only">Compare</span></TableHead>
                    <SortableHead col="game_title" label="Game" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="provider" label="Provider" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="num_players" label="Players" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    {METRICS.map(m => (
                      <SortableHead key={m.key} col={m.key} label={m.label.replace('Return ', '')} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    ))}
                    <SortableHead col="avg_bets_per_player" label="Avg Bets" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    <SortableHead col="avg_active_days_per_player" label="Avg Active Days" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    <SortableHead col="avg_turnover_eur_per_player" label="Avg Turnover" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={1 + 3 + METRICS.length + 3} className="text-center text-ink-dim text-xs py-8">
                        No games match these filters
                      </TableCell>
                    </TableRow>
                  ) : pagedRows.map(g => (
                    <TableRow
                      key={g.unique_game_id}
                      onClick={() => openPerformance(g)}
                      className="cursor-pointer"
                    >
                      {/* Ticking must not also trigger the row's open-in-performance click. */}
                      <TableCell className="w-8" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(g.unique_game_id)}
                          disabled={selectionFull && !selectedIds.has(g.unique_game_id)}
                          onChange={() => toggleCompare(g)}
                          aria-label={`Compare ${g.game_title}`}
                          className="accent-cta h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </TableCell>
                      <TableCell className="font-bold text-ink">{g.game_title}</TableCell>
                      <TableCell className="text-ink-dim">{g.provider}</TableCell>
                      <TableCell className="text-right">{fmtInt(g.num_players)}</TableCell>
                      {METRICS.map(m => (
                        <TableCell key={m.key} className="text-right">{fmtMetric(m.key, g[m.key])}</TableCell>
                      ))}
                      <TableCell className="text-right">{fmtAvg(g.avg_bets_per_player)}</TableCell>
                      <TableCell className="text-right">{fmtAvg(g.avg_active_days_per_player)}</TableCell>
                      <TableCell className="text-right">{fmtEur(g.avg_turnover_eur_per_player)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination page={page} totalPages={totalPages} total={tableRows.length} onPageChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
